import { FilePdfOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import {
  Breadcrumb,
  Button,
  Col,
  Input,
  Row,
  Select,
  Space,
  Table,
  Typography,
  message,
  DatePicker,
  Spin,
} from 'antd';
import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDiePressList, resetDiePressList } from '../../slices/die-press';
import { fetchProductionList } from '../../slices/production';
import { ReactComponent as Back } from '../../Images/Back.svg';
import { ReactComponent as EditIcon } from '../../Images/EditIcon.svg';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { fetchAndSetIpAddress } from '../../slices/ipSlice';
import { createActivityLog } from '../../slices/activity-log';
import eventBus from '../../utils/eventBus';

const { Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const ProductionList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [shiftFilter, setShiftFilter] = useState(null);
  const [pressFilter, setPressFilter] = useState(null);
  const [summaryData, setSummaryData] = useState('');
  const [page, setPage] = useState(1);
  const [pagesize, setPageSize] = useState(20);
  const [dateRange, setDateRange] = useState([
    dayjs().startOf('month'),
    dayjs(),
  ]);
  const {
    list: diePressList,
    loading: diePressLoading,
    currentpage: diePressCurrentPage,
    hasMore: diePressHasMore,
  } = useSelector(state => state.diePress);
  const ipAddress = useSelector(state => state.ip.ipAddress);
  useEffect(() => {
    if (!ipAddress) {
      dispatch(fetchAndSetIpAddress());
    }
  }, [ipAddress, dispatch]);

  const fetchProductionData = useCallback(() => {
    setLoading(true);

    const params = {
      page,
      pagesize,
      ...(search && { search }),
      ...(shiftFilter && { shift: shiftFilter }),
      ...(pressFilter && { press: pressFilter }),
    };

    if (dateRange?.[0] && dateRange?.[1]) {
      params.start_date = dateRange[0].format('YYYY-MM-DD');
      params.end_date = dateRange[1].format('YYYY-MM-DD');
    }

    dispatch(fetchProductionList(params))
      .unwrap()
      .then((response) => {
        setData(response?.results?.data || []);
        setSummaryData(
          response?.results?.production_duration_summary?.[0]?.total_hours || ''
        );

      })
      .catch((error) => {
        // optional: console.error(error);
        setData([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    dispatch,
    page,
    pagesize,
    search,
    dateRange,
    shiftFilter,
    pressFilter,
  ]);

  useEffect(() => {
    dispatch(fetchDiePressList({ page: 1, pagesize: 5000 }));
    dispatch(resetDiePressList());
    fetchProductionData();
  }, [fetchProductionData]);

  useEffect(() => {
    const handleReload = () => {
      fetchProductionData();
    };

    eventBus.on('financialYear:refresh', handleReload);

    return () => {
      eventBus.off('financialYear:refresh', handleReload);
    };
  }, [fetchProductionData]);

  const handleSearch = debounce(value => {
    setSearch(value);

  }, 500);

  const handlePageSizeChange = (current, size) => {
    setPage(1);
    setPageSize(size);
  };

  const exportToExcel = () => {
    try {
      const groupedData = {};
      data.forEach(item => {
        const shift = item.shift || 'Unknown';
        const press = item.press?.name || 'Unknown';
        const key = `${shift}-${press}`;

        if (!groupedData[key]) {
          groupedData[key] = {
            shift,
            press,
            data: [],
          };
        }
        groupedData[key].data.push(item);
      });

      console.log('Grouped data:', groupedData);

      const wb = XLSX.utils.book_new();

      const wsData = [];
      let currentRow = 0;

      Object.keys(groupedData).forEach((key, groupIndex) => {
        const group = groupedData[key];
        const shiftData = group.data;

        console.log(`Processing group ${key} with ${shiftData.length} records`);

        wsData[currentRow] = [
          'PRODUCTION REPORT',
          '',
          `DATE: ${dayjs().format('DD/MM/YYYY')}`,
          '',
          `SHIFT: ${group.shift}`,
          '',
          `PRESS: ${group.press}`,
        ];
        currentRow += 2;

        wsData[currentRow] = [
          'Sr.',
          'IWO No.',
          'Client',
          'Die No.',
          'Cavity',
          'Time In',
          'Time Out',
          'Total Cycle',
          'Billet Size',
          'Billet Nos',
          'Billet Weight',
          'Cut Length',
          'Pcs',
          'Weight/Pc',
          'Weight OUTPUT',
          'Recovery %',
          'Scrap',
          'Speed',
          'Kg/Hr',
          'Ext. Pressure',
          'Basket No.',
          'Alloy',
          'Grade',
          'Cast No.',
          'Target 400',
          'Bolster No.',
          'Remarks',
        ];
        currentRow++;

        let totalInput = 0;
        let totalOutput = 0;
        let totalScrap = 0;
        let totalPieces = 0;
        let totalKgHr = 0;

        shiftData.forEach((item, idx) => {
          const billetWeight = parseFloat(item.billet_weight || 0);
          const weight = parseFloat(item.weight || 0);
          const scrap = parseFloat(item.scrap || 0);
          const pieces = parseInt(item.pieces || 0);
          const kgHr = parseFloat(item.kg_per_hour || 0);

          totalInput += billetWeight;
          totalOutput += weight;
          totalScrap += scrap;
          totalPieces += pieces;
          totalKgHr += kgHr;

          wsData[currentRow] = [
            idx + 1,
            item.iwo_no?.order_no || '',
            item.customer?.customer_name || '',
            item.die_profile?.die_number || '',
            item.cavity || '',
            item.time_in || '',
            item.time_out || '',
            item.total_cycle || '',
            item.billet_size || '',
            item.billet_nos || '',
            billetWeight.toFixed(3),
            item.cut_length || '',
            pieces,
            parseFloat(item.weight_per_piece || 0).toFixed(3),
            weight.toFixed(3),
            parseFloat(item.recovery || 0).toFixed(2),
            scrap.toFixed(2),
            parseFloat(item.speed || 0).toFixed(2),
            kgHr.toFixed(2),
            parseFloat(item.ext_pressure || 0).toFixed(1),
            item.basket_no || '',
            item.alloy?.alloy_code || '',
            '',
            item.cast_no || '',
            '',
            item.bloster_no || '',
            item.remarks || '',
          ];
          currentRow++;
        });

        const recovery =
          totalInput > 0
            ? ((totalOutput / totalInput) * 100).toFixed(2)
            : '0.00';
        const avgKgHr =
          shiftData.length > 0
            ? (totalKgHr / shiftData.length).toFixed(2)
            : '0.00';

        wsData[currentRow] = [
          '', // Sr.
          '', // IWO No.
          '', // Client
          '', // Die No.
          '', // Cavity
          '', // Time In
          '', // Time Out
          'Total Hours', // Total Cycle
          '', // Billet Size
          '', // Billet Nos
          totalInput.toFixed(3), // Billet Weight
          '', // Cut Length
          totalPieces, // Pcs
          '', // Weight/Pc
          totalOutput.toFixed(3), // Weight OUTPUT
          recovery, // Recovery %
          totalScrap.toFixed(2), // Scrap
          '', // Speed
          avgKgHr, // Kg/Hr
          '', // Ext. Pressure
          '', // Basket No.
          '', // Alloy
          '', // Grade
          '', // Cast No.
          '', // Target 400
          '', // Bolster No.
          '', // Remarks
        ];
        currentRow++;
        wsData[currentRow] = [
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          `${summaryData}`,
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
        ];
        currentRow++;

        wsData[currentRow] = [
          `Total Input - ${group.shift}`,
          '',
          totalInput.toFixed(3),
        ];
        currentRow++;
        wsData[currentRow] = [
          `Total Output - ${group.shift}`,
          '',
          totalOutput.toFixed(3),
        ];
        currentRow++;
        wsData[currentRow] = ['Total Scrap', '', totalScrap.toFixed(2)];
        currentRow++;
        wsData[currentRow] = ['Recovery %', '', recovery];
        currentRow++;
        wsData[currentRow] = ['Kg/Hr', '', avgKgHr];
        currentRow += 3;

        const supervisor = shiftData[0]?.production_supervisor;
        if (supervisor) {
          wsData[currentRow] = ['PRODUCTION SUPERVISOR:', '', supervisor];
          currentRow += 2;
        }
      });

      const allData = Object.values(groupedData).flatMap(group => group.data);
      const grandTotals = allData.reduce(
        (acc, item) => ({
          input: acc.input + parseFloat(item.billet_weight || 0),
          output: acc.output + parseFloat(item.weight || 0),
          scrap: acc.scrap + parseFloat(item.scrap || 0),
          pieces: acc.pieces + parseInt(item.pieces || 0),
        }),
        { input: 0, output: 0, scrap: 0, pieces: 0 },
      );

      const grandRecovery =
        grandTotals.input > 0
          ? ((grandTotals.output / grandTotals.input) * 100).toFixed(2)
          : '0.00';

      wsData[currentRow] = ['Grand Total - A & B Shift'];
      currentRow++;
      wsData[currentRow] = [
        'Input',
        'Output',
        'Scrap',
        'Recovery %',
        'Total Pieces',
      ];
      currentRow++;
      wsData[currentRow] = [
        grandTotals.input.toFixed(3),
        grandTotals.output.toFixed(3),
        grandTotals.scrap.toFixed(2),
        grandRecovery,
        grandTotals.pieces,
      ];
      currentRow += 3;

      wsData[currentRow] = [
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ];

      console.log('Final worksheet data rows:', wsData.length);
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      const colWidths = Array(27).fill({ wch: 12 });
      ws['!cols'] = colWidths;
      XLSX.utils.book_append_sheet(wb, ws, 'Production Report');
      console.log('Workbook created, generating file...');
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const dataBlob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const fileName = `Production_Report_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`;
      saveAs(dataBlob, fileName);
      console.log('Excel file generated:', fileName);
      message.success('Excel file exported successfully');
      dispatch(
        createActivityLog({
          action: 'DOWNLOAD',
          module_name: 'Production',
          ip_address: ipAddress,
          discription: 'Production excel download successfully!',
        }),
      );
    } catch (error) {
      console.error('Excel export error:', error);
      message.error(`Failed to export Excel file: ${error.message}`);
    }
  };

  const columns = [
    {
      title: 'Production No.',
      dataIndex: 'production_no',
      key: 'production_no',
      width: 120,
      sorter: (a, b) => a.production_no.localeCompare(b.production_no),
    },
    {
      title: 'Shift',
      dataIndex: 'shift',
      key: 'shift',
      align: 'center',
    },
    {
      title: 'Press',
      dataIndex: 'press',
      key: 'press',
      align: 'center',
      render: record => {
        return <span>{record ? `${record?.name}` : 'N/A'}</span>;
      },
    },
    {
      title: 'IWO NO.',
      dataIndex: 'iwo_no',
      key: 'iwo_no',

      render: record => {
        return <span>{record ? `${record?.order_no}` : 'N/A'}</span>;
      },
    },
    {
      title: 'Customer',
      dataIndex: 'customer',
      key: 'customer',
      width: 100,
      render: record => {
        return <span>{record ? `${record?.customer_name}` : 'N/A'}</span>;
      },
    },
    {
      title: 'Profile No.',
      dataIndex: 'die_profile',
      key: 'die_profile',
      align: 'center',
      render: record => {
        return <span>{record ? `${record?.die_number}` : 'N/A'}</span>;
      },
    },
    {
      title: 'Cavity',
      dataIndex: 'cavity',
      key: 'cavity',
      align: 'center',
    },
    {
      title: 'Time In',
      dataIndex: 'time_in',
      key: 'time_in',
      align: 'center',
      render: record => {
        return <span>{record ? dayjs(record, "HH:mm:ss").format("hh:mm A") : 'N/A'}</span>;
      },
    },
    {
      title: 'Time Out',
      dataIndex: 'time_out',
      key: 'time_out',
      align: 'center',
      render: record => {
        return <span>{record ? dayjs(record, "HH:mm:ss").format("hh:mm A") : 'N/A'}</span>;
      },
    },
    {
      title: 'Total Cycle',
      dataIndex: 'total_cycle',
      key: 'total_cycle',
      align: 'center',
    },
    {
      title: 'Billet Size',
      dataIndex: 'billet_size',
      key: 'billet_size',
      align: 'center',
    },
    {
      title: 'Billet Nos',
      dataIndex: 'billet_nos',
      key: 'billet_nos',
      align: 'center',
    },
    {
      title: 'Billet Weight',
      dataIndex: 'billet_weight',
      key: 'billet_weight',
      align: 'center',
      render: value => parseFloat(value).toFixed(3),
    },
    {
      title: 'Cut Length',
      dataIndex: 'cut_length',
      key: 'cut_length',
      align: 'center',
    },
    {
      title: 'Pieces',
      dataIndex: 'pieces',
      key: 'pieces',
      align: 'center',
    },
    {
      title: 'Weight / Pc',
      dataIndex: 'weight_per_piece',
      key: 'weight_per_piece',
      align: 'center',
      render: value => parseFloat(value).toFixed(3),
    },
    {
      title: 'Weight Output',
      dataIndex: 'weight',
      key: 'weight',
      align: 'center',
      render: value => parseFloat(value).toFixed(3),
    },
    {
      title: 'Recovery %',
      dataIndex: 'recovery',
      key: 'recovery',
      align: 'center',
      render: value => parseFloat(value).toFixed(2),
    },
    {
      title: 'Scrap',
      dataIndex: 'scrap',
      key: 'scrap',
      align: 'center',
      render: value => value ? parseFloat(value).toFixed(2) : 0,
    },
    {
      title: 'Speed',
      dataIndex: 'speed',
      key: 'speed',
      align: 'center',
      render: value => parseFloat(value).toFixed(2),
    },
    {
      title: 'Kg / Hr',
      dataIndex: 'kg_per_hour',
      key: 'kg_per_hour',
      align: 'center',
      render: value => parseFloat(value).toFixed(2),
    },
    {
      title: 'Ext. Pressure',
      dataIndex: 'ext_pressure',
      key: 'ext_pressure',
      align: 'center',
      render: value => parseFloat(value).toFixed(1),
    },
    {
      title: 'Basket No.',
      dataIndex: 'basket_no',
      key: 'basket_no',
      align: 'center',
    },
    {
      title: 'Alloy',
      dataIndex: 'alloy',
      key: 'alloy',
      align: 'center',
      render: record => {
        return <span>{record ? `${record?.alloy_code}` : 'N/A'}</span>;
      },
    },
    // {
    //   title: 'Grade',
    //   dataIndex: 'grade',
    //   key: 'grade',
    //   align: 'center',
    // },
    {
      title: 'Cast No.',
      dataIndex: 'cast_no',
      key: 'cast_no',
      align: 'center',
    },
    // {
    //   title: 'Target 400',
    //   dataIndex: 'target',
    //   key: 'target',
    //   align: 'center',
    // },
    {
      title: 'Bolster No.',
      dataIndex: 'bloster_no',
      key: 'bloster_no',
      align: 'center',
    },
    {
      title: 'Remarks',
      dataIndex: 'remarks',
      key: 'remarks',
      align: 'center',
    },
    {
      title: 'Supervisor',
      dataIndex: 'production_supervisor',
      key: 'production_supervisor',
      align: 'center',
      render: value => value || 'N/A',
    },
    // {
    //   title: 'Power Cut At',
    //   dataIndex: 'power_cut_at',
    //   key: 'power_cut_at',
    //   align: 'center',
    //   render: value => value || 'N/A',
    // },
    // {
    //   title: 'Power Resumed At',
    //   dataIndex: 'power_resumed_at',
    //   key: 'power_resumed_at',
    //   align: 'center',
    //   render: value => value || 'N/A',
    // },
    // {
    //   title: 'Power Cut Duration',
    //   dataIndex: 'power_cut_duration',
    //   key: 'power_cut_duration',
    //   align: 'center',
    //   render: value => value || 'N/A',
    // },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: value => dayjs(value).format('DD/MM/YYYY HH:mm:ss'),
    },
    {
      title: 'Created By',
      dataIndex: 'created_by',
      key: 'created_by',
      render: value => `${value.first_name} ${value.last_name}`,
    },

    {
      title: 'Action',
      key: 'action',
      fixed: 'right',
      width: 80,
      render: (_, record) => (
        <Space>
          <EditIcon
            style={{ cursor: 'pointer' }}
            onClick={() => navigate(`/edit-production/${record.id}`)}
          />
        </Space>
      ),
    },
  ];

  const calculateSummaries = data => {
    const totals = data.reduce(
      (acc, record) => ({
        billet_weight:
          acc.billet_weight + parseFloat(record.billet_weight || 0),
        weight: acc.weight + parseFloat(record.weight || 0),
        scrap: acc.scrap + parseFloat(record.scrap || 0),
        kg_per_hour: acc.kg_per_hour + parseFloat(record.kg_per_hour || 0),
        count: acc.count + 1,
      }),
      { billet_weight: 0, weight: 0, scrap: 0, kg_per_hour: 0, count: 0 },
    );

    return {
      totalInput: totals.billet_weight.toFixed(3),
      totalOutput: totals.weight.toFixed(3),
      totalScrap: totals.scrap.toFixed(2),
      recovery:
        totals.billet_weight > 0
          ? ((totals.weight / totals.billet_weight) * 100).toFixed(2)
          : '0.00',
      avgKgPerHour:
        totals.count > 0
          ? (totals.kg_per_hour / totals.count).toFixed(2)
          : '0.00',
    };
  };

  const summaries = calculateSummaries(data);

  const handleDateRangeChange = dates => {
    if (dates) {
      setDateRange(dates);
    }
  };

  return (
    <div className="mainwrap">
      <div className="sticyaction">
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: '20px' }}
          className="pagenamerow mb-0"
        >
          <Col>
            <h2>Production List</h2>
            <div className="bredcrumbwrp">
              <Link onClick={() => navigate(-1)} className="back">
                <Back /> BACK
              </Link>
              <Breadcrumb
                items={[
                  { title: <Link to="/dashboard">Home</Link> },
                  { title: 'Production List' },
                ]}
              />
            </div>
          </Col>

          <Col>
            <Space size="middle">
              <div
                style={{
                  padding: '12px 20px',
                  background: '#f9f9f9',
                  borderRadius: '12px',
                  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)',
                  display: 'inline-block',
                  minWidth: '180px',
                  textAlign: 'center',
                }}
              >
                <Text
                  strong
                  style={{
                    fontSize: '18px',
                    color: '#0461b2fd',
                    marginTop: 4,
                    display: 'block',
                  }}
                >
                  <b>Today's Production :</b> {summaryData ?? '00:00:00'} hrs
                </Text>
              </div>

              <Input
                placeholder="Search"
                prefix={<SearchOutlined />}
                style={{ width: 250 }}
                onChange={e => handleSearch(e.target.value)}
                allowClear
              />
              <Button
                icon={<FilePdfOutlined />}
                type="primary"
                onClick={exportToExcel}
              >
                Export Excel
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/create-production')}
              >
                Create Production
              </Button>
            </Space>
          </Col>
        </Row>

      </div>
      <Row
        style={{
          padding: '20px',
          display: 'flex',
          justifyContent: 'end',
          gap: '10px',
        }}
      >
        <Col>
          <RangePicker
            value={dateRange}
            format="DD-MM-YYYY"
            onChange={handleDateRangeChange}
            style={{ width: '300px', marginRight: '10px' }}
            presets={{
              Today: [dayjs(), dayjs()],
              'This Week': [dayjs().startOf('week'), dayjs()],
              'This Month': [dayjs().startOf('month'), dayjs()],
              'Last Month': [
                dayjs().subtract(1, 'month').startOf('month'),
                dayjs().subtract(1, 'month').endOf('month'),
              ],
            }}
          />
        </Col>

        <Select
          placeholder="Filter by Shift"
          style={{ width: 150 }}
          allowClear
          onChange={value => {
            setShiftFilter(value);
          }}
        >
          <Option value="A">Shift A</Option>
          <Option value="B">Shift B</Option>
        </Select>
        <Select
          placeholder="Filter by Press"
          style={{ width: 150 }}
          allowClear
          onChange={value => {
            setPressFilter(value);
          }}
        >
          {diePressList.map(el => (
            <Option key={el.id} value={el.id}>
              {el.name}
            </Option>
          ))}
        </Select>
      </Row>
      <div
        // style={{
        //   borderRadius: '8px',
        //   boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        // }}
        className="tablewrp"
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px', width: '100%' }}>
            <Spin size="large" />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={data}

            onShowSizeChange={handlePageSizeChange}
            rowKey="id"
            bordered
            size="middle"
            // scroll={{ x: '3500px' }}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row
                  style={{ background: '#fafafa', fontWeight: 'bold' }}
                >
                  <Table.Summary.Cell colSpan={12}>Total</Table.Summary.Cell>
                  <Table.Summary.Cell align="center">
                    {summaries.totalInput}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell colSpan={3}></Table.Summary.Cell>
                  <Table.Summary.Cell align="center">
                    {summaries.totalOutput}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell align="center">
                    {/* {summaries.recovery} */}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell align="center">
                    {/* {summaries.totalScrap} */}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell colSpan={2}></Table.Summary.Cell>
                  <Table.Summary.Cell align="center">
                    {/* {summaries.avgKgPerHour} */}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell colSpan={13}></Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        )}
      </div>
      <style jsx>{`
        .ant-table-thead > tr > th {
          background-color: #f0f2f5;
          font-weight: 600;
          color: #1a1a1a;
        }
        .ant-table-row:hover {
          background-color: #e6f7ff;
        }
        .ant-table-row:nth-child(even) {
          background-color: #fafafa;
        }
        .ant-table-row:nth-child(odd) {
          background-color: #ffffff;
        }
        .ant-card {
          border-radius: 8px;
        }
        .ant-btn-primary {
          border-radius: 4px;
        }
      `}</style>
    </div>
  );
};

export default ProductionList;